import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage, languages } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2, Mail, Lock, Eye, EyeOff, Wallet, Globe } from 'lucide-react';
import '@/App.css';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const { login } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const message = err.response?.data?.detail || t('invalidCredentials');
      setError(message);
      if (message.includes('verify')) {
        setTimeout(() => navigate('/verify-email', { state: { email } }), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  const currentLang = languages.find(l => l.code === language);

  return (
    <div className="min-h-screen flex bg-zinc-950">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 z-50">
        <div className="relative">
          <button
            onClick={() => setShowLangMenu(!showLangMenu)}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-900/80 border border-zinc-800 rounded-lg text-zinc-300 hover:bg-zinc-800 transition-colors"
            data-testid="language-selector"
          >
            <Globe className="h-4 w-4" />
            <span>{currentLang?.flag} {currentLang?.name}</span>
          </button>
          
          {showLangMenu && (
            <div className="absolute right-0 mt-2 w-40 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl overflow-hidden">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => { changeLanguage(lang.code); setShowLangMenu(false); }}
                  className={`w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-zinc-800 transition-colors ${
                    language === lang.code ? 'bg-purple-600/20 text-purple-400' : 'text-zinc-300'
                  }`}
                  data-testid={`lang-${lang.code}`}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 relative">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-600/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-violet-600/20 rounded-full blur-3xl"></div>
        </div>
        
        <Card className="w-full max-w-md bg-zinc-900/80 border-zinc-800 backdrop-blur-xl relative z-10 shadow-2xl shadow-purple-900/20">
          <CardHeader className="space-y-1 text-center pb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30">
                <Wallet className="h-6 w-6 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl font-black tracking-tight">
              <span className="bg-gradient-to-r from-purple-400 via-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                WSFinances
              </span>
            </CardTitle>
            <CardDescription className="text-zinc-400">
              {t('loginTitle')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm animate-fade-in">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-300 text-sm font-medium">{t('email')}</Label>
                <div className="relative group">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-zinc-500 group-focus-within:text-purple-400 transition-colors" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-11 h-12 bg-zinc-950/50 border-zinc-800 focus:border-purple-500 focus:ring-purple-500/20 transition-all"
                    data-testid="login-email-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300 text-sm font-medium">{t('password')}</Label>
                <div className="relative group">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-zinc-500 group-focus-within:text-purple-400 transition-colors" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-11 pr-11 h-12 bg-zinc-950/50 border-zinc-800 focus:border-purple-500 focus:ring-purple-500/20 transition-all"
                    data-testid="login-password-input"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-zinc-500 hover:text-purple-400 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                  data-testid="forgot-password-link"
                >
                  {t('forgotPassword')}
                </Link>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white font-semibold shadow-lg shadow-purple-500/25 transition-all hover:shadow-purple-500/40"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('loggingIn')}
                  </>
                ) : (
                  t('login')
                )}
              </Button>

              <p className="text-center text-sm text-zinc-400 pt-2">
                {t('noAccount')}{' '}
                <Link 
                  to="/register" 
                  className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
                  data-testid="register-link"
                >
                  {t('register')}
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 via-violet-900/40 to-fuchsia-900/40"></div>
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&w=1200&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-transparent to-transparent"></div>
        
        <div className="absolute bottom-12 left-12 right-12 z-10">
          <div className="bg-zinc-900/60 backdrop-blur-xl rounded-2xl p-8 border border-zinc-800/50">
            <h2 className="text-4xl font-black text-white mb-4">
              {t('heroTitle')} <span className="bg-gradient-to-r from-purple-400 to-fuchsia-400 bg-clip-text text-transparent">{t('heroHighlight')}</span>
            </h2>
            <p className="text-zinc-300 text-lg">
              {t('heroDesc')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
